import { GetPlaceDetails_getPlaceDetails } from 'api/places/types/GetPlaceDetails';
export const checkIsUSorCA = (input: string | null): boolean => {
  return (
    Boolean(input === 'United States' || input === 'Canada') ||
    Boolean(input === 'US' || input === 'CA')
  );
};

export const joinLocation = (input: (string | undefined | null)[]): string => {
  return input.filter(Boolean).join(', ');
};

export const createLocation = (
  latitude?: number | null,
  longitude?: number | null
): {
  latitude: number;
  longitude: number;
} | null =>
  latitude != null && longitude != null ? { latitude, longitude } : null;

type LocationLike = {
  country?: string | null;
  state?: string | null;
  [key: string]: unknown;
};

/**
 * For each location, blank out `state` unless the country is US/CA. Places
 * API returns a non-null state for many other countries (London → "ENG",
 * Mexico City → "CDMX"), but `storeDetails.state` is only populated for
 * US/CA athletes, so including those values would AND a NULL DB column
 * with a non-empty value and exclude valid matches.
 *
 * Returns a new array; inputs are not mutated. Generic over the location
 * shape so it works for any object with `country`/`state` fields.
 */
export const sanitizeLocations = <T extends LocationLike>(
  locations?: T[] | null
): T[] =>
  (locations || []).map((loc) =>
    checkIsUSorCA(loc.country ?? null) ? loc : { ...loc, state: '' }
  );

export const handlePlaceSelect = (
  setFieldsValue: (values: any) => void,
  placeDetails: GetPlaceDetails_getPlaceDetails,
  type: 'hometown' | 'city'
): string => {
  const isUSOrCA = checkIsUSorCA(placeDetails.country);

  const fieldMap = {
    hometown: {
      city: 'hometown',
      state: 'hometownState',
      country: 'hometownCountry',
      latitude: 'hometownLatitude',
      longitude: 'hometownLongitude',
    },
    city: {
      city: 'city',
      state: 'state',
      country: 'country',
      location: 'location',
      latitude: 'latitude',
      longitude: 'longitude',
    },
  };

  setFieldsValue({
    [fieldMap[type].city]: placeDetails.city,
    [fieldMap[type].state]: isUSOrCA ? placeDetails.stateCode : null,
    [fieldMap[type].country]: placeDetails.countryCode,
    [fieldMap[type].latitude]: placeDetails.location.latitude,
    [fieldMap[type].longitude]: placeDetails.location.longitude,
  });

  return joinLocation([
    placeDetails.city,
    isUSOrCA ? placeDetails.stateCode : null,
    placeDetails.countryCode,
  ]);
};
